import { TASK_PRIORITIES, type TaskPriority } from "@sts/schemas";
import type { Task } from "../api/types.js";

/**
 * Task vocabulary, and the one thing about a task list the portal decides for
 * itself.
 *
 * The ORDER is not one of those things. `apps/api/src/crm/tasks.ts` sorts urgent
 * before high before normal and, within a priority, soonest due first with
 * undated tasks last — which already puts the late work at the top of its band,
 * because an overdue task is by definition the one with the earliest due date.
 * Re-sorting here would produce a portal that disagreed with `GET /api/tasks`
 * about what needs attention, and there is no version of that disagreement where
 * the portal is right. The screens render the list in the order it arrives.
 *
 * What the portal does decide is how a priority is SHOWN, which is below.
 */

export const PRIORITY_LABEL: Record<TaskPriority, string> = {
  urgent: "Urgent",
  high: "High",
  normal: "Normal",
};

/**
 * A shape per priority, carried alongside the word.
 *
 * Both are here because neither is enough on its own. Colour is not enough:
 * some of the people who read this board every day cannot tell the amber row
 * from the red one, and it is printed in black and white for site meetings. The
 * word alone is enough to read but not to scan — a column of "Urgent / High /
 * Normal" all sets the same width and weight — so the mark gives the eye
 * something to count down. The mark is `aria-hidden` wherever it is rendered:
 * a screen reader should say "urgent", not "black up-pointing triangle twice".
 */
export const PRIORITY_MARK: Record<TaskPriority, string> = {
  urgent: "▲▲",
  high: "▲",
  normal: "–",
};

/** Highest first — the order the API sorts in, and the order a filter offers. */
export const PRIORITY_ORDER: readonly TaskPriority[] = [...TASK_PRIORITIES].sort(
  (a, b) => rank(a) - rank(b),
);

function rank(priority: TaskPriority): number {
  return priority === "urgent" ? 0 : priority === "high" ? 1 : 2;
}

/**
 * Today, as the calendar day to compare a due date against.
 *
 * UTC rather than local. Every date this application handles is a calendar day
 * the database decided — `format.ts` already refuses to put a `date` column
 * through a local-time `Date` for exactly this reason — and the failure modes
 * are not symmetric: reading "today" an hour early would mark work overdue that
 * is not, which is the version that erodes trust in the board.
 */
export function todayUtc(clock: Date = new Date()): string {
  return clock.toISOString().slice(0, 10);
}

/**
 * Past its due date and not yet done.
 *
 * Compared as text, not as parsed instants. `due_at` arrives as Postgres' own
 * rendering of a `timestamptz` — "2026-09-01 00:00:00+00", not an ISO string —
 * and `Date.parse` on that shape is implementation-defined. Two ISO calendar
 * days compare correctly as strings, which is both exactly right and impossible
 * for a timezone to move.
 *
 * A task with no due date is never overdue. That is not a technicality: "no date
 * was agreed" and "the agreed date has passed" are different states, and
 * treating the first as late would fill the board with work nobody is waiting on.
 */
export function isOverdue(task: Task, today: string): boolean {
  if (task.completed_at !== null) return false;
  if (task.due_at === null) return false;
  return task.due_at.slice(0, 10) < today;
}

export interface TaskTally {
  readonly open: number;
  readonly overdue: number;
  readonly urgent: number;
}

/** What the list on screen adds up to, as opposed to the organisation's totals. */
export function tally(tasks: readonly Task[], today: string): TaskTally {
  let open = 0;
  let overdue = 0;
  let urgent = 0;
  for (const task of tasks) {
    if (task.completed_at === null) open++;
    if (isOverdue(task, today)) overdue++;
    if (task.completed_at === null && task.priority === "urgent") urgent++;
  }
  return { open, overdue, urgent };
}

/**
 * Who a task is on, in words.
 *
 * The raw user id is useless to a reader and is the only thing the API returns,
 * so the one id the portal can resolve — its own, from `GET /api/me` — is
 * resolved, and the rest are shown as what they are: somebody else.
 */
export function assigneeLabel(
  task: Task,
  myUserId: string | null,
): { readonly text: string; readonly isMine: boolean } {
  if (task.assigned_to_user_id === null) {
    return { text: "Unassigned", isMine: false };
  }
  if (myUserId !== null && task.assigned_to_user_id === myUserId) {
    return { text: "You", isMine: true };
  }
  return { text: "A colleague", isMine: false };
}
